import React from "react";
import {Link} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Edit, Add, Visibility, Delete} from "@material-ui/icons";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../util/ApiHelper";
import {CustomCheckbox} from "../../../util";
import {useLoading} from "../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../util/useManualPagination";
import dayjs from "dayjs";
import PaginationBarForHandler from "../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
    imageCell: {
        width: "60px",
        height: "60px",
        objectFit: "cover",
        borderRadius: "4px",
    },
}));

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const formatDatetime = (dt) => {
    if (!dt) return "-";
    return dayjs(dt).format("DD MMM YYYY");
};

const MerchandiseListingPage = (props) => {
    const {match, disabled} = props;
    const classes = useStyles();
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);

    const {setLoading} = useLoading();

    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );


    const columns = React.useMemo(
        () => [
            {
                Header: "ID",
                accessor: "id",
                Cell: ({row}) => {
                    return (
                        <span className={classes.text}>
                            {row.original.id}
                        </span>
                    );
                },
            },
            {
                Header: "Image",
                accessor: "image",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return row.original.image ? (
                        <img 
                            src={row.original.image} 
                            alt="Merchandise" 
                            className={classes.imageCell}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    ) : (
                        <span>-</span>
                    );
                },
            },
            {
                Header: "Title",
                accessor: "title",
            },
            {
                Header: "Sequence",
                accessor: "sequence",
                Cell: ({ row }) => {
                    return <span>{row.original.sequence ?? "-"}</span>;
                },
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({row}) => {
                    const status = row.original.status;
                    return (
                        <span style={{
                            color: status === "ACTIVE" || status === true ? "#4caf50" : "#999999"
                        }}>
                            {status === "ACTIVE" || status === true ? "Active" : "Inactive"}
                        </span>
                    );
                },
            },
            {
                Header: "Update Time",
                accessor: "updateTime",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return formatDatetime(row.original.updateTime);
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex", gap: "8px"}}>
                            <IconButton
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}/edit`}
                                title="Edit"
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteMerchandise(row.original.id, row.original.title || "Merchandise")}
                                title="Delete"
                                style={{ color: '#f44336' }}
                            >
                                <Delete fontSize="small"/>
                            </IconButton>
                        </div>
                    ),
                },
            ]);
        }
    );

    const getMerchandise = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.cineclub.getMerchandise(
                paginationHandler.pageIndex,
                paginationHandler.pageSize * paginationHandler.pageIndex,
                paginationHandler.pageSize,
                false
            );
            console.log('MERCHANDISE RESULT:', result)  
            
            var output = result.data.content.map(function(x){
                x.id = x.id.toString();
                return x;
            });
            
            paginationHandler.setPageIndex(0).setRows(output);
            const all = result.data.totalElements;
            const totalPages = result.data.totalPages;

            setElementCounts({
                all
            });
            setData(paginationHandler.setPageIndex(0).getRows());
            console.log(paginationHandler.pageSize);
            setTotalPages(Math.ceil(result.data.totalElements/paginationHandler.pageSize));
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) {
            getMerchandise(); // only get merchandise if paginationHandler ready
        }
    }, [getMerchandise, paginationHandler]);

    const triggerSearch = React.useCallback(() => {
        setLoading(true);
        console.log("KEYWORD: "+keyword);
        paginationHandler
            .resetRows()
            .setQueryString(["title","id"], keyword)
            .filterRowsByAll();
        setData(paginationHandler.setPageIndex(0).getRows());
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, keyword, paginationHandler, setLoading]);

    const handleDeleteMerchandise = React.useCallback(async (merchandiseId, merchandiseTitle) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the merchandise "${merchandiseTitle}"? This action cannot be undone.`
        );
        
        if (!confirmed) {
            return;
        }

        try {
            setLoading(true);
            const response = await apiHelper.cineclub.deleteMerchandise(merchandiseId);
            
            if (response.data && response.data.code === 0) {
                // Success - refresh the merchandise list
                await getMerchandise();
                alert("Merchandise deleted successfully!");
            } else {
                console.error("Failed to delete merchandise:", response);
                alert("Failed to delete merchandise. Please try again.");
            }
        } catch (error) {
            console.error("Error deleting merchandise:", error);
            alert("An error occurred while deleting the merchandise. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [getMerchandise, setLoading]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                {"Cineclub > Merchandise List"}
            </Typography>
            <form>
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex" style={{ marginRight: "10px" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={Link}
                                    to={`${match.url}/create`}
                                    startIcon={<Add />}
                                >
                                    Create Merchandise
                                </Button>
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "1%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "30%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "15%"}} />
                                    <col
                                        style={{width: "9%"}} />
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, index) => {
                                        prepareRow(row);
                                        return (
                                            <TableRow {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                        <TableCell
                                                            {...cell.getCellProps()}
                                                            className={`${cell.column.className ?? ""}`}
                                                            style={{
                                                                paddingTop: 6,
                                                                paddingBottom: 6,
                                                                lineHeight: "1.1em",
                                                                height: "48px",
                                                            }}
                                                        >
                                                            {cell.render("Cell")}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={null}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
};

export default MerchandiseListingPage;

